export type CoToastPayload = {
    id?: string;
    title?: string;
    message?: string;
    to?: string;
    icon?: string;
    duration?: number;
    timestamp?: number | string | Date;
    playsSound?: boolean;
};

type ChatToastMessage = {
    from?: string | { Profile?: { Name?: string } };
    thread_id?: string | number;
    timestamp?: number | string | Date;
    ts?: number | string | Date;
    text?: string;
};

export type CoToastItem = {
    id: string;
    title: string;
    message: string;
    to: string;
    icon: string;
    duration: number;
    timestamp: number | null;
    playsSound: boolean;
};

type ToastListener = (toast: CoToastItem) => void;
type ListUsersByIDs = (payload: { ids: string }) => Promise<{ Users?: Array<{ Profile?: { Name?: string } }> }>;
type Translate = (key: string) => string;
const DEFAULT_TOAST_DURATION = 5000;
const HIDDEN_PROFILE_NAME = 'User with hidden profile';
const TOAST_MESSAGE_PREVIEW_MAX_CHARS = 24;
const listeners = new Set<ToastListener>();

function normalizeTimestamp(timestamp?: number | string | Date): number | null {
    if (timestamp instanceof Date) {
        return timestamp.getTime();
    }

    const parsedTimestamp = Number(timestamp);
    if (!parsedTimestamp) {
        return null;
    }

    return parsedTimestamp < 1000000000000 ? parsedTimestamp * 1000 : parsedTimestamp;
}

function createToastId() {
    return `co-toast-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeToast(payload: CoToastPayload): CoToastItem {
    return {
        id: payload.id || createToastId(),
        title: payload.title || '',
        message: payload.message || '',
        to: payload.to || '',
        icon: payload.icon || 'chat-dots',
        duration: typeof payload.duration === 'number' ? payload.duration : DEFAULT_TOAST_DURATION,
        timestamp: normalizeTimestamp(payload.timestamp),
        playsSound: Boolean(payload.playsSound),
    };
}

export function showToast(payload: CoToastPayload): CoToastItem {
    const toast = normalizeToast(payload);

    listeners.forEach((listener) => listener(toast));

    return toast;
}

export function subscribeToToasts(listener: ToastListener) {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
}

function createIdsPayload(userId: string) {
    return JSON.stringify({ IDS: [{ ID: userId }] });
}

function getSenderName(user?: { Profile?: { Name?: string } }) {
    return user?.Profile?.Name || HIDDEN_PROFILE_NAME;
}

function truncateText(text: string, maxChars: number) {
    if (text.length <= maxChars) {
        return text;
    }

    return `${text.slice(0, maxChars).trim()}...`;
}

function getChatMessagePreview(messageText?: string) {
    if (!messageText) {
        return '';
    }

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const richTextContainer = document.createElement('div');
        richTextContainer.innerHTML = messageText;

        return truncateText(
            (richTextContainer.textContent || richTextContainer.innerText || '').replace(/\s+/g, ' ').trim(),
            TOAST_MESSAGE_PREVIEW_MAX_CHARS
        );
    }

    return truncateText(
        messageText
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim(),
        TOAST_MESSAGE_PREVIEW_MAX_CHARS
    );
}

export function createToastService(config: {
    listUsersByIDs: ListUsersByIDs;
    t: Translate;
    logger?: Pick<Console, 'log'>;
}) {
    const { listUsersByIDs, t, logger = console } = config;

    return {
        show: showToast,
        async showChatMessage(message: ChatToastMessage) {
            const userId = typeof message?.from === 'string' ? message.from : '';
            let senderName = getSenderName(typeof message?.from === 'object' ? message.from : undefined);
            const messagePreview = getChatMessagePreview(message?.text);

            if (userId) {
                try {
                    const response = await listUsersByIDs({ ids: createIdsPayload(userId) });
                    senderName = getSenderName(response?.Users?.[0]);
                } catch (error) {
                    logger.log(error);
                }
            }

            return showToast({
                title: senderName,
                message: messagePreview,
                icon: 'send',
                timestamp: message?.timestamp || message?.ts,
                to: message?.thread_id ? `/messenger/${message.thread_id}` : '',
                playsSound: true,
            });
        },
    };
}
